import Head from 'next/head'
import Link from 'next/link'

export default function CookiePolicyPage() {
  return (
    <>
    <Head>
      <title>Cookie Policy — SkillBets</title>
      <meta name="description" content="Learn how SkillBets uses cookies to improve your experience on our sports prediction analytics platform." />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="SkillBets" />
      <meta property="og:locale" content="en_ZA" />
      <meta property="og:title" content="Cookie Policy — SkillBets" />
      <meta property="og:description" content="Learn how SkillBets uses cookies to improve your experience on our sports prediction analytics platform." />
      <link rel="canonical" href="https://skillbets.co.za/cookies/" />
      <meta property="og:url" content="https://skillbets.co.za/cookies" />
      <meta property="og:image" content="https://skillbets.co.za/og-image.png" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@SkillBetsZA" />
      <meta name="twitter:title" content="Cookie Policy — SkillBets" />
      <meta name="twitter:description" content="Learn how SkillBets uses cookies to improve your experience on our sports prediction analytics platform." />
      <meta name="twitter:image" content="https://skillbets.co.za/og-image.png" />
    </Head>
    <main className="min-h-screen bg-gray-900 text-white">
      {/* Compliance ribbon */}
      <div className="w-full bg-black/40 px-4 py-2 text-center text-[11px] text-white/70">
        Simulation & analytics only • No cash wagering or payouts • SkillBets is not a bookmaker
      </div>

      {/* Header */}
      <header className="border-b border-gray-800 bg-gray-900">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-4 py-4">
          <Link href="/login" className="flex items-center">
            <img src="/skillbets-logo.png" alt="SkillBets" className="h-14" />
          </Link>
          <Link
            href="/login"
            className="rounded-xl bg-[#3EA8F5] px-3 py-2 text-xs font-bold text-gray-900 hover:brightness-110"
            data-testid="button-back-home"
          >
            Back to Home
          </Link>
        </div>
      </header>

      {/* Body */}
      <article className="mx-auto max-w-5xl px-4 py-10">
        <h1 className="text-3xl sm:text-4xl font-extrabold">Cookie Policy</h1>
        <p className="mt-3 max-w-3xl text-white/70">
          This Cookie Policy explains how SkillBets ("we", "us") uses cookies and similar technologies on our website and
          services (the "Service"). It should be read together with our <a className="text-[#3EA8F5] underline" href="/privacy">Privacy Policy</a> and
          <a className="text-[#3EA8F5] underline ml-1" href="/terms">Terms &amp; Conditions</a>.
        </p>
        <p className="mt-2 text-xs text-white/50" suppressHydrationWarning>Last updated: 2025-01-01</p>

        <section className="prose prose-invert mt-8 max-w-none">
          <h2 className="text-2xl font-bold mt-8 mb-4">1. What are cookies?</h2>
          <p className="text-white/80">
            Cookies are small text files that a website stores on your device. They are widely used to make websites work,
            to remember preferences, and to provide aggregated analytics. We also use local storage and similar
            technologies for related purposes.
          </p>

          <h2 className="text-2xl font-bold mt-8 mb-4">2. How we use cookies</h2>
          <ul className="list-disc pl-5 text-white/80 space-y-2">
            <li><strong>Operate the Service:</strong> Keep you signed in, route requests reliably, and secure your session.</li>
            <li><strong>Remember preferences:</strong> For example, cookie consent choices, UI settings, and locale/timezone.</li>
            <li><strong>Analytics:</strong> Understand usage patterns so we can improve features and performance.</li>
          </ul>

          <h2 className="text-2xl font-bold mt-8 mb-4">3. Types of cookies we use</h2>
          <ul className="list-disc pl-5 text-white/80 space-y-2">
            <li>
              <strong>Strictly Necessary (Essential):</strong> Required for the Service to function (authentication/session, load
              balancing, security). You cannot opt out of these via the banner because the site will not work without them.
            </li>
            <li>
              <strong>Preferences (Functional):</strong> Remember choices like cookie consent, interface preferences, and locale.
            </li>
            <li>
              <strong>Analytics (Performance):</strong> Help us measure and improve site usage. Data is aggregated or pseudonymous; we
              do not use analytics to build personal profiles for advertising.
            </li>
          </ul>
          <p className="text-white/80 mt-2">
            <strong>We do not use advertising/marketing cookies</strong> for ad targeting or retargeting.
          </p>

          <h2 className="text-2xl font-bold mt-8 mb-4">4. Cookies we set (first‑party)</h2>
          <p className="text-white/70">Exact names may vary by release. Typical examples include:</p>
          <div className="overflow-x-auto mt-4">
            <table className="min-w-full text-sm border-collapse">
              <thead>
                <tr className="text-left">
                  <th className="border-b border-white/10 py-2 pr-4 text-white">Name</th>
                  <th className="border-b border-white/10 py-2 pr-4 text-white">Purpose</th>
                  <th className="border-b border-white/10 py-2 pr-4 text-white">Category</th>
                  <th className="border-b border-white/10 py-2 text-white">Expiry</th>
                </tr>
              </thead>
              <tbody className="align-top text-white/80">
                <tr>
                  <td className="py-2 pr-4">sb_session, sb_csrf</td>
                  <td className="py-2 pr-4">Authenticate you, maintain a secure session, prevent CSRF.</td>
                  <td className="py-2 pr-4">Strictly Necessary</td>
                  <td className="py-2">Session / &lt;= 1 day</td>
                </tr>
                <tr>
                  <td className="py-2 pr-4">sb_cookie_consent</td>
                  <td className="py-2 pr-4">Stores your cookie banner choices (accept/reject/manage).</td>
                  <td className="py-2 pr-4">Preferences</td>
                  <td className="py-2">6–12 months</td>
                </tr>
                <tr>
                  <td className="py-2 pr-4">sb_pref_*</td>
                  <td className="py-2 pr-4">UI preferences (e.g., collapsed panels, locale, theme).</td>
                  <td className="py-2 pr-4">Preferences</td>
                  <td className="py-2">3–12 months</td>
                </tr>
                <tr>
                  <td className="py-2 pr-4">sb_analytics_*</td>
                  <td className="py-2 pr-4">Anonymous/aggregated usage stats to improve the Service.</td>
                  <td className="py-2 pr-4">Analytics</td>
                  <td className="py-2">Up to 13 months</td>
                </tr>
              </tbody>
            </table>
          </div>

          <h2 className="text-2xl font-bold mt-8 mb-4">5. Third‑party cookies</h2>
          <p className="text-white/80">
            We may use third‑party analytics providers that set their own cookies to help us measure performance (for
            example, page views, feature adoption, and error rates). These providers act on our instructions where
            applicable and may provide their own privacy/cookie notices. You can refuse analytics via our banner (where
            offered) and/or through your browser settings.
          </p>

          <h2 className="text-2xl font-bold mt-8 mb-4">6. Your choices</h2>
          <ul className="list-disc pl-5 text-white/80 space-y-2">
            <li>
              <strong>Cookie banner:</strong> On your first visit, you can choose <em>Accept all</em>, <em>Reject non‑essential</em>, or
              <em> Manage settings</em> (where available). You can change your choice at any time via the <em>Cookie Settings</em>
              link in the footer (if implemented) or by clearing cookies in your browser.
            </li>
            <li>
              <strong>Browser controls:</strong> Most browsers let you block or delete cookies. Check your browser's help pages for
              instructions. Blocking essential cookies may break core functionality such as sign‑in.
            </li>
            <li>
              <strong>Do Not Track (DNT):</strong> Some browsers send a DNT signal. At this time, the Service does not respond to DNT
              signals.
            </li>
          </ul>

          <h2 className="text-2xl font-bold mt-8 mb-4">7. Retention</h2>
          <p className="text-white/80">
            Cookie lifetimes depend on their purpose and your choices. Session cookies expire when you close your browser;
            persistent cookies last for a set period unless you delete them sooner. We aim to keep retention aligned with
            the purposes described above.
          </p>

          <h2 className="text-2xl font-bold mt-8 mb-4">8. Updates to this Policy</h2>
          <p className="text-white/80">
            We may update this Cookie Policy from time to time. If changes are material, we will provide reasonable
            notice (for example, via a banner or on this page). Your continued use after changes take effect constitutes
            acceptance of the updated Policy.
          </p>

          <h2 className="text-2xl font-bold mt-8 mb-4">9. Contact</h2>
          <p className="text-white/80">
            If you have questions about this Cookie Policy, contact us at
            <a className="text-[#3EA8F5] underline ml-1" href="mailto:support@skillbets.co.za">support@skillbets.co.za</a>.
          </p>
        </section>
      </article>
    </main>
    </>
  );
}
